import { frontendErrorsRange, type FrontendErrorsPreset, type FrontendErrorsRange } from "@/features/observability/hooks/frontend-errors-range";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import type {
    FrontendErrorsCategory,
    FrontendErrorsQueryParams,
    FrontendErrorsSource,
} from "@/features/observability/types/frontend-errors.types";

export type FrontendErrorsRangeChoice = FrontendErrorsPreset | "custom";

export type FrontendErrorsFilters = {
    workspaceId: string;
    range: FrontendErrorsRangeChoice;
    customFrom: number | null;
    customTo: number | null;
    source: FrontendErrorsSource;
    category: FrontendErrorsCategory | null;
    errorCode: string | null;
    release: string | null;
};

export type FrontendErrorsResolvedRange = {
    range: FrontendErrorsRange | null;
    clamped: boolean;
};

// The reader only answers for the workspaces it allows; the screen offers exactly those.
const SUPPORTED_WORKSPACES: string[] = ["enviaDinero"];
const DEFAULT_RANGE: FrontendErrorsPreset = "24h";
const PRESETS: FrontendErrorsPreset[] = ["1h", "24h", "7d"];
const DIMENSION_MAX_LENGTH = 64;

const isPreset = (value: string | null): value is FrontendErrorsPreset =>
    value !== null && PRESETS.some((preset) => preset === value);

const toEpoch = (value: string | null): number | null => {
    if (value === null || !/^\d{1,15}$/.test(value)) return null;
    return Number(value);
};

const toDimension = (value: string | null): string | null => {
    if (value === null) return null;
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed.length > DIMENSION_MAX_LENGTH) return null;
    return trimmed;
};

// Explicit presets win; `range=custom` keeps the free mode with whichever bounds are present, so a half-typed
// window never collapses to the default; without `range`, a numeric from/to pair (epoch ms, as the command-center
// links send it) is a custom window; anything else falls back to the default preset.
const rangeOf = (search: URLSearchParams): Pick<FrontendErrorsFilters, "range" | "customFrom" | "customTo"> => {
    const range = search.get("range");
    if (isPreset(range)) return { range, customFrom: null, customTo: null };
    const customFrom = toEpoch(search.get("from"));
    const customTo = toEpoch(search.get("to"));
    if (range === "custom") return { range: "custom", customFrom, customTo };
    if (customFrom === null || customTo === null) return { range: DEFAULT_RANGE, customFrom: null, customTo: null };
    return { range: "custom", customFrom, customTo };
};

const parse = (search: URLSearchParams): FrontendErrorsFilters => {
    const workspace = search.get("workspaceId");
    const category = search.get("category");
    return {
        workspaceId: workspace !== null && SUPPORTED_WORKSPACES.includes(workspace) ? workspace : SUPPORTED_WORKSPACES[0],
        ...rangeOf(search),
        source: search.get("source") === "backend" ? "backend" : "frontend",
        category: frontendErrorsFormat.isCategory(category) ? category : null,
        errorCode: toDimension(search.get("errorCode")),
        release: toDimension(search.get("release")),
    };
};

const toSearch = (filters: FrontendErrorsFilters): URLSearchParams => {
    const search = new URLSearchParams();
    search.set("workspaceId", filters.workspaceId);
    search.set("source", filters.source);
    if (filters.range === "custom") {
        search.set("range", "custom");
        if (filters.customFrom !== null) search.set("from", String(filters.customFrom));
        if (filters.customTo !== null) search.set("to", String(filters.customTo));
    } else if (filters.range !== DEFAULT_RANGE) {
        search.set("range", filters.range);
    }
    if (filters.category !== null) search.set("category", filters.category);
    if (filters.errorCode !== null) search.set("errorCode", filters.errorCode);
    if (filters.release !== null) search.set("release", filters.release);
    return search;
};

// A custom window keeps its most recent part: at most 7 days, never before the 29 retained days, never in the future.
const resolveRange = (filters: FrontendErrorsFilters, now: number): FrontendErrorsResolvedRange => {
    if (filters.range !== "custom") return { range: frontendErrorsRange.presetRange(filters.range, now), clamped: false };
    if (filters.customFrom === null || filters.customTo === null || filters.customFrom >= filters.customTo) {
        return { range: null, clamped: false };
    }
    const requested = { from: filters.customFrom, to: Math.min(filters.customTo, now) };
    const range = frontendErrorsRange.clampRange(requested, now);
    if (range === null) return { range: null, clamped: true };
    // Capping a future end at now is not a trim of the asked window; moving its start is.
    return { range, clamped: range.from !== filters.customFrom };
};

const toQuery = (filters: FrontendErrorsFilters, range: FrontendErrorsRange): FrontendErrorsQueryParams => ({
    workspaceId: filters.workspaceId,
    source: filters.source,
    from: range.from,
    to: range.to,
    category: filters.category,
    errorCode: filters.errorCode,
    release: filters.release,
});

export const frontendErrorsFilters = {
    SUPPORTED_WORKSPACES,
    parse,
    toSearch,
    toDimension,
    resolveRange,
    toQuery,
};
